<template>
  <div id="CustomSelect" class="dmh-confirm-container w-100 mb-0 p-0">
    <label class="dmh-confirm-field">{{ question.field_label }}</label>
    <div class="select-display" @click="toggleDropdown">
      <span>{{ selectedLabel }}</span>
      <i class="fa fa-chevron-down arrow"></i>
    </div>

    <ul
      v-if="isOpen"
      ref="options"
      class="select-options"
      :class="{ 'opens-upward': opensUpward }"
      :style="{ maxHeight: maxHeight }"
    >
      <li
        v-for="(option, index) in question.options"
        :key="index"
        @click="selectOption(option)"
        :class="{ selected: option === value }"
      >
        <span class="checkbox"></span>
        <span>{{ option }}</span>
      </li>
    </ul>
  </div>
</template>

<script>
export default {
  name: "CustomSelect",
  props: ["question", "answer"],
  data() {
    return {
      isOpen: false,
      opensUpward: false,
      maxHeight: null,
      value: this.answer[this.question.field_key] || null
    };
  },
  computed: {
    selectedLabel() {
      const selected = this.question.options.find(opt => opt === this.value);
      return selected ? selected : "Select...";
    },
  },
  methods: {
    toggleDropdown() {
      if (this.isOpen) {
        this.closeDropdown();
      } else {
        this.openDropdown();
      }
    },
    openDropdown() {
      this.isOpen = true;
      this.opensUpward = false;
      this.maxHeight = null;
      document.addEventListener("click", this.closeOnOutsideClick);
      document.addEventListener("keydown", this.closeOnEscape);
      window.addEventListener("resize", this.fitInViewport);
      // Measure after render so the list's natural height is known.
      this.$nextTick(this.fitInViewport);
    },
    closeDropdown() {
      this.isOpen = false;
      document.removeEventListener("click", this.closeOnOutsideClick);
      document.removeEventListener("keydown", this.closeOnEscape);
      window.removeEventListener("resize", this.fitInViewport);
    },
    closeOnOutsideClick(event) {
      if (!this.$el.contains(event.target)) {
        this.closeDropdown();
      }
    },
    closeOnEscape(event) {
      if (event.key === "Escape") {
        this.closeDropdown();
      }
    },
    // Open below the select when the list fits, otherwise on whichever side has more
    // room, scrolling inside the list if neither side fits it.
    fitInViewport() {
      const optionsElement = this.$refs.options;
      if (!optionsElement) {
        return;
      }
      const viewportMargin = 16;
      const { top, bottom } = this.$el.getBoundingClientRect();
      const spaceBelow = window.innerHeight - bottom - viewportMargin;
      const spaceAbove = top - viewportMargin;
      // Natural height: clear any previous cap before measuring.
      this.maxHeight = null;
      this.opensUpward = false;
      this.$nextTick(() => {
        const listHeight = optionsElement.scrollHeight;
        this.opensUpward = listHeight > spaceBelow && spaceAbove > spaceBelow;
        const availableSpace = this.opensUpward ? spaceAbove : spaceBelow;
        this.maxHeight = listHeight > availableSpace ? `${Math.max(availableSpace, 120)}px` : null;
      });
    },
    selectOption(option) {
      this.value = option;
      this.closeDropdown();
      this.answer[this.question.field_key] = option;
    },
  },
  beforeDestroy() {
    this.closeDropdown();
  },
};
</script>

<style lang="less">
#CustomSelect {
  position: relative;
  font-family: sans-serif;

  .dmh-confirm-field {
    padding: 15px 15px 0;
  }
  
  .select-display {
    padding: 0 15px 15px;
    border-radius: 4px;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;

    span {
      color: #1e1e22;
      font-family: var(--font-regular, "DMSans");
      font-size: 1.6rem;
    }
  }
  
  .arrow {
    padding: 12px;
    background-color: rgba(30, 30, 34, 0.05);
    border-radius: 12px;
  }

  .select-options {
    width: 100%;
    display: flex;
    font-size: 1.6rem;
    flex-direction: column;
    gap: 16px;
    list-style: none;
    margin: 0;
    padding: 20px 30px;
    border: 1px solid rgba(174, 176, 189, 1);
    border-radius: 5px;
    top: 100%;
    position: absolute;
    background: white;
    z-index: 1000;
    overflow-y: auto;

    &.opens-upward {
      top: auto;
      bottom: 100%;
    }
  }
  
  .select-options li {
    cursor: pointer;
    display: flex;
    align-items: center;
    font-family: var(--font-regular, "DMSans");
  }

  .checkbox {
    display: inline-block;
    border-radius: 50%;
    width: 18px;
    height: 18px;
    border: 2px solid rgba(132, 129, 138, 1);
    margin-right: 15px;
  }
  
  .select-options li.selected {
    color: var(--brand-primary);

    .checkbox {
      background-color: var(--brand-primary);
      border-color: var(--brand-primary);
    }
  }
}
</style>
